import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { clientStyles } from './PreviewPanel';

interface EmailExporterProps {
  html: string;
  open: boolean;
  onClose: () => void;
  isPro?: boolean;
}

export const EmailExporter: React.FC<EmailExporterProps> = ({ html, open, onClose, isPro = false }) => {
  const previewRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [isExporting, setIsExporting] = useState(false);

  const clientList = [
    { id: 'gmail', name: 'Gmail' },
    { id: 'outlook', name: 'Outlook' },
    { id: 'apple', name: 'Apple Mail' },
    { id: 'yahoo', name: 'Yahoo Mail' },
    { id: 'thunderbird', name: 'Thunderbird' }
  ];

  const addWatermark = (canvas: HTMLCanvasElement) => {
    if (isPro) return canvas;

    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    // Save the current context state
    ctx.save();
    
    // Calculate dimensions for the watermark background
    const width = canvas.width;
    const height = canvas.height;
    
    // Create gradient background for better visibility
    const gradient = ctx.createLinearGradient(0, height * 0.4, 0, height * 0.6);
    gradient.addColorStop(0, 'rgba(99, 102, 241, 0.02)');
    gradient.addColorStop(0.5, 'rgba(99, 102, 241, 0.08)');
    gradient.addColorStop(1, 'rgba(99, 102, 241, 0.02)');
    
    // Fill the gradient strip
    ctx.fillStyle = gradient;
    ctx.fillRect(0, height * 0.4, width, height * 0.2);

    // Set up the text style
    const fontSize = Math.min(width * 0.08, 48); // Responsive font size with max limit
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.fillStyle = 'rgba(99, 102, 241, 0.2)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Draw the text in the center
    ctx.fillText('Powered by Mailzen', width / 2, height / 2);
    
    // Restore the context state
    ctx.restore();
    return canvas;
  };

  const transformHtmlForClient = (html: string, clientId: string): string => {
    const style = clientStyles[clientId];
    if (!style) return html;

    const quirks = style.quirks;
    let transformedHtml = html;

    // Add client-specific meta tags
    const metaTags = `
      <meta name="color-scheme" content="light dark">
      <meta name="supported-color-schemes" content="light dark">
      ${clientId === 'outlook' ? '<meta http-equiv="X-UA-Compatible" content="IE=edge">' : ''}
      ${clientId === 'apple' ? '<meta name="x-apple-disable-message-reformatting">' : ''}
    `;

    // Add client-specific styles
    const clientStyle = `
      <style>
        body {
          font-family: ${style.fontFamily};
          margin: 0;
          padding: 20px;
          background-color: ${style.backgroundColor};
        }
        .email-container {
          max-width: ${style.maxWidth};
          margin: ${style.margin};
        }
        img {
          ${quirks.imageDisplay ? `display: ${quirks.imageDisplay};` : ''}
          max-width: 100%;
          height: auto;
        }
        ${quirks.needsTableFix ? `
          table {
            border-collapse: collapse;
            mso-table-lspace: ${quirks.msoTableLspace || '0pt'};
            mso-table-rspace: ${quirks.msoTableRspace || '0pt'};
          }
        ` : ''}
        ${!quirks.supportsWebFonts ? `
          @font-face {
            font-family: 'fallback';
            src: local('Arial');
          }
        ` : ''}
        @media (prefers-color-scheme: dark) {
          body {
            background-color: ${style.darkMode.backgroundColor};
            color: ${style.darkMode.color};
          }
        }
      </style>
    `;

    // Insert meta tags and styles
    transformedHtml = transformedHtml.replace('</head>', `${metaTags}${clientStyle}</head>`);

    // Wrap content in container
    transformedHtml = transformedHtml.replace('<body>', '<body><div class="email-container">');
    transformedHtml = transformedHtml.replace('</body>', '</div></body>');

    return transformedHtml;
  };

  const exportPreviews = async () => {
    setIsExporting(true);
    const zip = new JSZip();
    
    try {
      // Generate previews for each client
      const previews = await Promise.all(
        clientList.map(async (client) => {
          const previewElement = previewRefs.current[client.id];
          if (!previewElement) return null;

          const canvas = await html2canvas(previewElement, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            width: previewElement.offsetWidth,
            height: previewElement.offsetHeight,
            windowWidth: previewElement.offsetWidth,
            windowHeight: previewElement.offsetHeight
          });

          // Add watermark for free users
          const watermarkedCanvas = addWatermark(canvas);

          return {
            id: client.id,
            name: client.name,
            blob: await new Promise<Blob>((resolve) => {
              watermarkedCanvas.toBlob((blob: Blob | null) => {
                resolve(blob as Blob);
              }, 'image/png');
            })
          };
        })
      );

      // Add previews to zip file
      previews.forEach((preview) => {
        if (preview && preview.blob) {
          zip.file(`${preview.name.toLowerCase().replace(' ', '-')}-preview.png`, preview.blob);
        }
      });

      // Generate and save zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, 'email-previews.zip');
    } catch (error) {
      console.error('Error exporting previews:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Export Email Previews
            {!isPro && (
              <Typography 
                component="span" 
                variant="body2" 
                sx={{ 
                  ml: 2,
                  color: 'text.secondary',
                  fontStyle: 'italic'
                }}
              >
                Free preview with watermark
              </Typography>
            )}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {clientList.map((client) => (
            <Grid item xs={12} md={6} key={client.id}>
              <Box
                ref={(el: HTMLDivElement | null) => (previewRefs.current[client.id] = el)}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  height: '300px',
                  overflow: 'hidden',
                  backgroundColor: 'white',
                  position: 'relative'
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  {client.name}
                </Typography>
                <Box sx={{ 
                  height: 'calc(100% - 32px)',
                  overflow: 'auto',
                  position: 'relative'
                }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: transformHtmlForClient(html, client.id)
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={exportPreviews}
          variant="contained"
          startIcon={isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
          disabled={isExporting}
        >
          {isExporting ? 'Exporting...' : 'Export All Previews'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 